import { isTacticAppWebView } from "../../helpers/src/isTacticAppWebView";
import { getTacticAppAuthToken } from "../../helpers/src/webview/getTacticAppAuthToken";
import { Client } from "./client";
import { AuthenticatedUser } from "./entities/authenticated_user";
import { Settings } from "./settings";

export class TacticApiClient {
  private static _instance: TacticApiClient;
  protected settings: Settings;
  protected user: AuthenticatedUser | null;
  public client: Client;

  constructor(
    settings: Settings,
    public embeddedSettings: Settings
  ) {
    if (TacticApiClient._instance) {
      throw new Error("Singleton cannot be instantiated more than once");
    }
    this.settings = settings;
    this.client = new Client(this.settings);
    this.user = null;
    TacticApiClient._instance = this;
  }

  public cookieExists() {
    return true;
  }

  public setEmbeddedSettings() {
    this.settings = this.embeddedSettings;
    this.client = new Client(this.settings);
  }

  public updateSlug(slug: string) {
    this.settings = { ...this.settings, slug };
    this.client = new Client(this.settings);
  }

  public updateAuthHeader(authHeader: string) {
    this.settings = { ...this.settings, authHeader };
    this.client = new Client(this.settings);
  }

  public setInvitationCode(invitationCode: string | undefined) {
    this.settings = { ...this.settings, invitationCode };
    this.client = new Client(this.settings);
  }

  public setPublicVisitsCode(publicVisitsCode: string | undefined) {
    this.settings = { ...this.settings, publicVisitsCode };
    this.client = new Client(this.settings);
  }

  public setTicketShareCode(ticketShareCode: string | undefined) {
    this.settings = { ...this.settings, ticketShareCode };
    this.client = new Client(this.settings);
  }

  public setStationId(stationId: string | undefined) {
    this.settings = { ...this.settings, stationId };
    this.client = new Client(this.settings);
  }

  public updateUser(authenticatedUser: AuthenticatedUser) {
    this.user = authenticatedUser;
    if (!authenticatedUser.organization) {
      throw new Error("Organization is required");
    }
    const slug = authenticatedUser.organization.slug;
    this.settings = { ...this.settings, slug };
    this.client = new Client(this.settings);
  }

  public overrideConfig(settings: Settings, user: AuthenticatedUser) {
    this.settings = settings;
    this.client = new Client(this.settings);
    this.user = user;
  }
}

export const initApi = (defaultSettings: Settings) => {
  const settings: Settings = { ...defaultSettings };

  if (settings.debugAuth && settings.debugAuthAccess) {
    settings.legacyAuth = {
      access: settings.debugAuthAccess,
      impersonation: null,
      idProvider: "Id"
    };
  }

  if (settings.unionDevAuth) {
    settings.authHeader = settings.unionDevAuth;
  }

  const embeddedSettings: Settings = {
    environment: settings.environment,
    slug: null,
    url: settings.url,
    debug: settings.debug,
    debugAuth: settings.debugAuth,
    debugGet: settings.debugGet,
    protocol: settings.protocol,
    domain: settings.domain,
    authHeader: settings.authHeader,
    corsBypass: settings.corsBypass,
    onError: (error: unknown, status?: number) => {
      console.error("error handler:", error, status);
    }
  };

  const api = new TacticApiClient(settings, embeddedSettings);

  if (isTacticAppWebView()) {
    settings.authHeader = getTacticAppAuthToken();
  }

  return { settings, embeddedSettings, api };
};
